import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { useSearchParams } from "react-router"
import {
  useGlobalSubstanceDetail,
  useSubstanceDetail,
} from "../../../api/substancesApi"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import type { FieldDef } from "../../shared/FieldsGrid"
import { FieldsGrid } from "../../shared/FieldsGrid"
import { FieldsSectionHeader } from "../../shared/FieldsSectionHeader"
import { FieldsSectionSkeleton } from "../../shared/FieldsSectionSkeleton"
import { McPill } from "../../shared/McPill"

/** "org" reads the org-scoped detail endpoint (404s for a substance with no
 *  links to this org's articles); "catalog" reads the global, org-visibility
 *  -scoped endpoint instead — required for browsing catalog substances that
 *  have zero links in this org, which the org-scoped endpoint 404s on. */
type SubstanceDetailView = "org" | "catalog"

function useSubstanceForView(
  substanceId: string | null,
  view: SubstanceDetailView,
) {
  const orgQuery = useSubstanceDetail(view === "org" ? substanceId : null)
  const catalogQuery = useGlobalSubstanceDetail(
    view === "catalog" ? substanceId : null,
  )
  return view === "catalog" ? catalogQuery : orgQuery
}

export function SubstanceFieldsHeader({ view }: { view: SubstanceDetailView }) {
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const substanceId = searchParams.get("id")

  const { data: substance, isLoading } = useSubstanceForView(substanceId, view)

  if (isLoading) return <FieldsSectionSkeleton count={2} />
  if (!substance) return null

  return (
    <FieldsSectionHeader
      label={translate("MC_MD_TAB_SUBSTANCES")}
      name={substance.name}
      subtitle={substance.casNumber ?? substance.ecNumber ?? undefined}
    />
  )
}

export function SubstanceFieldsBody({ view }: { view: SubstanceDetailView }) {
  const { translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const substanceId = searchParams.get("id")

  const { data: substance, isLoading } = useSubstanceForView(substanceId, view)

  if (isLoading) return <FieldsSectionSkeleton count={5} />
  if (!substance) return null

  const fields: FieldDef[] = [
    {
      label: translate("MC_MD_PANEL_COL_CAS"),
      value: substance.casNumber ? (
        <span className="font-mono">{substance.casNumber}</span>
      ) : (
        <span className="text-grey5">—</span>
      ),
    },
    {
      label: translate("MC_MD_FIELD_EC_NUMBER"),
      value: substance.ecNumber ? (
        <span className="font-mono">{substance.ecNumber}</span>
      ) : (
        <span className="text-grey5">—</span>
      ),
    },
    { label: translate("MC_MD_FIELD_NAME"), value: substance.name },
  ]

  if (substance.fullName) {
    fields.push({
      label: translate("MC_MD_SUBSTANCE_CREATE_FULL_NAME_LABEL"),
      value: substance.fullName,
    })
  }

  if (substance.description) {
    fields.push({
      label: translate("MC_MD_SUBSTANCE_CREATE_DESCRIPTION_LABEL"),
      value: substance.description,
    })
  }

  // Plain "is this on the list" pill — never a compliance verdict. Six of
  // the seven regulatory fields share this exact shape; a helper collapses
  // the duplication AND removes the copy-paste-onto-the-wrong-flag risk a
  // hand-written sixth block would otherwise carry.
  const yesNoField = (
    labelKey: TranslationKeys,
    listed: boolean,
  ): FieldDef => ({
    label: translate(labelKey),
    value: listed ? (
      <McPill tone="danger">{translate("MC_MD_PILL_YES")}</McPill>
    ) : (
      <McPill tone="muted">{translate("MC_MD_PILL_NO")}</McPill>
    ),
  })

  fields.push(
    {
      label: translate("MC_MD_FIELD_REACH_LISTING"),
      value: substance.svhcListed ? (
        <McPill tone="danger">{translate("MC_MD_PILL_CANDIDATE")}</McPill>
      ) : (
        <McPill tone="muted">—</McPill>
      ),
    },
    yesNoField("MC_MD_FIELD_ROHS_ANNEX_II", substance.rohsListed),
    yesNoField("MC_MD_FIELD_SVHC", substance.svhcListed),
    yesNoField("MC_MD_FRAMEWORK_REACH_ANNEX_XVII", substance.annexXviiListed),
    yesNoField("MC_MD_FRAMEWORK_REACH_ANNEX_XIV", substance.annexXivListed),
    yesNoField("MC_MD_FRAMEWORK_POPS", substance.popsListed),
    yesNoField("MC_MD_FRAMEWORK_CMR", substance.cmrListed),
  )

  if (substance.alternativeNames && substance.alternativeNames.length > 0) {
    fields.push({
      label: translate("MC_MD_FIELD_ALTERNATIVE_NAMES"),
      value: substance.alternativeNames.join(", "),
    })
  }

  return (
    <div className="flex flex-col">
      <CollapsibleSection label={translate("MC_MD_FIELDS_SECTION")}>
        <FieldsGrid fields={fields} />
      </CollapsibleSection>
    </div>
  )
}
